import { z } from "zod";

import { ChatEntrySchema, HandoffResolutionSchema, HandoffStatusSchema } from "./chat.js";
import { ApprovalStatusSchema, ReviewInstructionsSchema } from "./guards.js";
import { AttachmentSchema, BlobIdSchema, EntryPartSchema, MimeTypeSchema } from "./parts.js";
import { SessionKindSchema } from "./sessions.js";

export { AttachmentSchema, type Attachment } from "./parts.js";

/** Request / response bodies for the gateway HTTP API (ARCHITECTURE §5.5). */

export const HealthResponseSchema = z.object({
  ok: z.boolean(),
  version: z.string().min(1),
  uptimeMs: z.number().nonnegative(),
}).strict();
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/**
 * GET /idle (HOSTED §6.1): is anything in the box running, or about to? A
 * readout of what the hub would wait on to settle, counted box-wide.
 * `busy` is any count above zero. `nextWakeAt` (epoch ms, the soonest armed
 * routine) does not feed it: the caller decides how soon is too soon.
 */
export const IdleResponseSchema = z.object({
  busy: z.boolean(),
  /** Agent and room turns running, and contact-thread turns running. */
  runningTurns: z.number().int().nonnegative(),
  /** Turns queued behind a running one, and contact threads waiting out their debounce. */
  queuedTurns: z.number().int().nonnegative(),
  /** Background subagent tasks. */
  openChildSessions: z.number().int().nonnegative(),
  /** Post-turn memory and episode writes, and contact messages being recorded. */
  backgroundWork: z.number().int().nonnegative(),
  /** Approval cards waiting on the user (HOSTED §6.2). */
  pendingApprovals: z.number().int().nonnegative(),
  /**
   * True when those cards are all that keeps the box busy: every running turn
   * and child session is waiting on one, and nothing is queued or writing.
   * A card waits however long the user takes, and the turn behind it counts in
   * `runningTurns` too, so this is how a caller tells a box parked on the user
   * from a box at work.
   */
  heldOnlyByApprovals: z.boolean(),
  /**
   * When the newest of those cards was raised (epoch ms), or null. Stopping the
   * box expires every card (nothing runs, and the agent is told so), so a
   * caller that stops or moves a box held only by approvals waits until even
   * the newest has waited long enough.
   */
  newestApprovalAt: z.number().int().nonnegative().nullable(),
  nextWakeAt: z.number().int().nonnegative().nullable(),
}).strict();
export type IdleResponse = z.infer<typeof IdleResponseSchema>;

/**
 * An agent's face (ARCHITECTURE §6.11): either a picture the user uploaded,
 * positioned with a zoom and an offset, or a mark — a shape, a colour and a
 * short label. Unset means the mark derived from the agent's name and id, so
 * an agent always has one.
 */
/** The outline a face is cut to; a mark is filled in it, a picture is clipped by it. */
export const AvatarShapeSchema = z.enum(["circle", "rounded", "square", "oval", "triangle", "star", "heart", "hexagon"]);
export type AvatarShape = z.infer<typeof AvatarShapeSchema>;

export const AvatarSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("image"),
      blobId: BlobIdSchema,
      /** Raster only; SVG is refused because a same-origin SVG can carry script. */
      mimeType: z.enum(["image/png", "image/jpeg", "image/webp", "image/gif"]),
      /** The outline; a circle when unset. */
      shape: AvatarShapeSchema.optional(),
      /**
       * `free`: the picture is placed by its centre, `x` and `y` in halves of
       * the frame from the frame's centre, so it can sit partly outside; zoom
       * is its width in frames. Unset (the older records): zoom is 1 or more,
       * and `x`/`y` in -1..1 slide the picture between its own edges.
       */
      frame: z.literal("free").optional(),
      zoom: z.number().min(0.25).max(4).optional(),
      x: z.number().min(-3).max(3).optional(),
      y: z.number().min(-3).max(3).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("mark"),
      shape: AvatarShapeSchema,
      color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "a colour is #rrggbb"),
      /** An initial or two, or an emoji; empty falls back to the agent's initials. */
      label: z.string().max(8).optional(),
    })
    .strict(),
]);
export type Avatar = z.infer<typeof AvatarSchema>;

/**
 * The agent's profile, shaped like the prototype's: a short job title for the
 * sidebar and a persona description for the model. There is deliberately no
 * system prompt override; the description is the only user text in the prompt.
 */
export const AgentConfigSchema = z.object({
  /** Short label, e.g. "Personal Assistant". Display only; never sent to the model. */
  title: z.string().optional(),
  /** Persona and remit in the user's words; folded into the system prompt's profile section. */
  description: z.string().optional(),
  mainModel: z.string().optional(),
  cheapModel: z.string().optional(),
  /** Display only; never sent to the model. */
  avatar: AvatarSchema.optional(),
  /** Whether this agent may raise an OS notification (§6.14); on unless set false. */
  notify: z.boolean().optional(),
  /** The context window this agent condenses at, in tokens: 25k to 150k, 50k when unset, and never above the box's own. */
  contextTokens: z.number().int().min(25_000).max(150_000).optional(),
  /** Reasoning effort on this agent's own turns; the box's ARK_BOT_EFFORT_MAIN when unset, the provider's default when neither is set. */
  effort: z.enum(["minimal", "low", "medium", "high"]).optional(),
}).strict();
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

/**
 * An `agent` is one assistant with its own model, box and memory. A `group`
 * is a room several agents speak in: it has a name, a description and a
 * member list, but no model of its own (ARCHITECTURE §6.8). The prototype
 * makes the same choice — a group is a sidebar row like any other, so chats,
 * events and routes work on it unchanged.
 */
export const AgentKindSchema = z.enum(["agent", "group"]);
export type AgentKind = z.infer<typeof AgentKindSchema>;

export const AgentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  kind: AgentKindSchema.default("agent"),
  /** For a group: the agents in the room, in speaking order. Empty for an agent. */
  members: z.array(z.string().min(1)).default([]),
  config: AgentConfigSchema,
  createdAt: z.number().int().nonnegative(),
}).strict();
export type Agent = z.infer<typeof AgentSchema>;

/**
 * An agent as the sidebar shows it (the prototype's roster row): the agent,
 * what was last said in its chat, when it last moved, and whether a turn is
 * running. `updatedAt` is the newest chat entry's time, or the agent's
 * creation when nothing has been said. Streamed from `GET /roster`, which
 * opens with the whole list and then follows every agent, so the sidebar
 * never polls.
 */
export const RosterAgentSchema = AgentSchema.extend({
  updatedAt: z.number().int().nonnegative(),
  lastEntry: ChatEntrySchema.optional(),
  busy: z.boolean(),
}).strict();
export type RosterAgent = z.infer<typeof RosterAgentSchema>;

export const RosterEventSchema = z.discriminatedUnion("type", [
  /** The first frame of a `GET /roster` connection: everything, so a reconnect needs no cursor. */
  z.object({ type: z.literal("roster"), agents: z.array(RosterAgentSchema) }).strict(),
  /** An agent was made, changed, or its chat moved; the row is sent whole. */
  z.object({ type: z.literal("agent"), agent: RosterAgentSchema }).strict(),
  z.object({ type: z.literal("agent.deleted"), id: z.string().min(1) }).strict(),
]);
export type RosterEvent = z.infer<typeof RosterEventSchema>;

export const CreateAgentRequestSchema = z.object({
  name: z.string().min(1),
  kind: AgentKindSchema.optional(),
  /** For a group: who is in it. Unknown ids and groups are dropped, and the list is capped. */
  members: z.array(z.string().min(1)).optional(),
  config: AgentConfigSchema.default({}),
  /** Open the conversation: the new agent says hello on its own, as a created prototype agent does. */
  introduce: z.boolean().optional(),
}).strict();
export type CreateAgentRequest = z.infer<typeof CreateAgentRequestSchema>;

/** PATCH /agents/:id. `config` and `members`, when present, replace the whole value. */
export const UpdateAgentRequestSchema = z.object({
  name: z.string().min(1).optional(),
  members: z.array(z.string().min(1)).optional(),
  config: AgentConfigSchema.optional(),
}).strict();
export type UpdateAgentRequest = z.infer<typeof UpdateAgentRequestSchema>;

export const PostMessageRequestSchema = z.object({
  text: z.string(),
  attachments: z.array(AttachmentSchema).optional(),
  /** The id of the chat entry this message answers; the agent is shown that entry's words with the message. */
  replyTo: z.string().optional(),
}).strict();
export type PostMessageRequest = z.infer<typeof PostMessageRequestSchema>;

/** The turn the message opened; a turn already running was interrupted for it. */
export const PostMessageResponseSchema = z.object({ turnId: z.string().min(1) }).strict();
export type PostMessageResponse = z.infer<typeof PostMessageResponseSchema>;

export const InterruptResponseSchema = z.object({ interrupted: z.boolean() }).strict();
export type InterruptResponse = z.infer<typeof InterruptResponseSchema>;

/**
 * POST /agents/:id/compact: the earlier part of the agent's conversation,
 * condensed now rather than when the window fills. `condensedEntries` is 0
 * when there was nothing before the last exchange to condense; `context` is
 * the gauge after it. 409 while a turn is running.
 */
export const CompactResponseSchema = z.object({ condensedEntries: z.number().int().nonnegative(), context: z.object({ inputTokens: z.number().int().nonnegative(), outputTokens: z.number().int().nonnegative(), windowTokens: z.number().int().positive() }).strict() }).strict();
export type CompactResponse = z.infer<typeof CompactResponseSchema>;

/** `allowRule` goes with an approval only: the rule is added to the always-allow list before the call runs. */
export const ApprovalRequestSchema = z
  .object({
    approved: z.boolean(),
    note: z.string().optional(),
    allowRule: z.string().min(1).optional(),
  })
  .strict()
  .refine((request) => request.approved || request.allowRule == null, { message: "a rule can only be added when approving" });
export type ApprovalRequest = z.infer<typeof ApprovalRequestSchema>;

/** POST /approvals/:id: the card's state after the answer. A card no longer pending answers 409. */
export const ApprovalResponseSchema = z.object({ approvalId: z.string().min(1), status: ApprovalStatusSchema }).strict();
export type ApprovalResponse = z.infer<typeof ApprovalResponseSchema>;

/**
 * POST /agents/:id/handoff: the user answering the agent's request for the
 * box, either having done the step or having declined it. There is at most one
 * waiting request per agent, so the agent is the address, as it is in the
 * prototype's hand-back command.
 */
export const HandoffRequestSchema = z.object({ resolution: HandoffResolutionSchema }).strict();
export type HandoffRequest = z.infer<typeof HandoffRequestSchema>;

/** The card as it stands after the answer; 404 when no request was waiting. */
export const HandoffResponseSchema = z.object({ id: z.string().min(1), status: HandoffStatusSchema }).strict();
export type HandoffResponse = z.infer<typeof HandoffResponseSchema>;

/**
 * POST /agents/:id/forms/:formId: the user answering a form, or dismissing it
 * (ARCHITECTURE §6.12). Values for fields the form never asked are dropped.
 */
export const SubmitFormRequestSchema = z
  .object({
    values: z.record(z.string(), z.string()).optional(),
    /** The user closed the card without answering; the agent is told so. */
    cancelled: z.boolean().optional(),
  })
  .strict();
export type SubmitFormRequest = z.infer<typeof SubmitFormRequestSchema>;

/**
 * GET /settings: the host settings the user edits, one file under the data
 * volume. PATCH takes any subset, each section whole. `timeZone` is the IANA
 * zone every routine schedule is read in (ARCHITECTURE §6.9); unset means the
 * host's own zone, which in the box is UTC.
 */
/**
 * How the sidebar is arranged, kept on the host so it is the same wherever
 * the app is opened, as the prototype keeps its pinned and sectioned bots:
 * the pinned rail in order, the sections each with the agents in them, and
 * the agents hidden from the list. An agent in no section is unassigned.
 */
export const SidebarSectionSchema = z.object({ id: z.string().min(1), name: z.string().min(1), agents: z.array(z.string().min(1)) }).strict();
export type SidebarSection = z.infer<typeof SidebarSectionSchema>;
export const SidebarSettingsSchema = z.object({ pinned: z.array(z.string().min(1)), hidden: z.array(z.string().min(1)), sections: z.array(SidebarSectionSchema) }).strict();
export type SidebarSettings = z.infer<typeof SidebarSettingsSchema>;

/**
 * `onboarded`: the first-run tour has been taken or skipped on this host, so
 * it opens once per workspace rather than once per browser, as the
 * prototype keeps its onboarding flag per account. Unset until then.
 */
export const SettingsSchema = z.object({ review: ReviewInstructionsSchema, timeZone: z.string().min(1).optional(), sidebar: SidebarSettingsSchema.optional(), onboarded: z.boolean().optional() }).strict();
export type Settings = z.infer<typeof SettingsSchema>;
export const UpdateSettingsRequestSchema = SettingsSchema.partial();
export type UpdateSettingsRequest = z.infer<typeof UpdateSettingsRequestSchema>;

/**
 * One thing worth telling the user about away from the app (ARCHITECTURE
 * §6.14): the agent said something, or it needs them. Streamed from
 * `GET /notifications` across every agent, live only — a notification that
 * arrives an hour late is noise, so there is no replay cursor.
 */
export const NotificationKindSchema = z.enum(["message", "needs_input"]);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

export const NotificationSchema = z.object({
  agentId: z.string().min(1),
  agentName: z.string().min(1),
  kind: NotificationKindSchema,
  /** One line: what happened, in the words the user would use. */
  body: z.string().min(1),
  at: z.number().int().nonnegative(),
}).strict();
export type Notification = z.infer<typeof NotificationSchema>;

/**
 * A phone that wants the same notifications pushed to it when no app is open
 * (client-app/ANDROID.md §5): the token FCM gave it, kept in the box's data
 * volume so it rides the snapshot and survives a machine move. The box never
 * sends anything itself; a host beside it (the UI server, the machine agent)
 * reads this list and holds the credential.
 */
export const PUSH_TOKEN_MAX_CHARS = 4096;
export const PUSH_DEVICE_MAX_CHARS = 80;
export const PUSH_MAX_SUBSCRIPTIONS = 50;

export const PushPlatformSchema = z.enum(["fcm"]);
export type PushPlatform = z.infer<typeof PushPlatformSchema>;

export const RegisterPushSubscriptionRequestSchema = z.object({
  platform: PushPlatformSchema,
  token: z.string().min(1).max(PUSH_TOKEN_MAX_CHARS),
  /** What to call the phone in a list, e.g. "Pixel 9"; display only. */
  device: z.string().max(PUSH_DEVICE_MAX_CHARS).optional(),
}).strict();
export type RegisterPushSubscriptionRequest = z.infer<typeof RegisterPushSubscriptionRequestSchema>;

export const PushSubscriptionSchema = RegisterPushSubscriptionRequestSchema.extend({
  registeredAt: z.number().int().nonnegative(),
}).strict();
export type PushSubscription = z.infer<typeof PushSubscriptionSchema>;

export const PushSubscriptionsResponseSchema = z.object({ subscriptions: z.array(PushSubscriptionSchema) }).strict();
export type PushSubscriptionsResponse = z.infer<typeof PushSubscriptionsResponseSchema>;

export const EntryRoleSchema = z.enum(["user", "assistant", "tool", "system"]);
export type EntryRole = z.infer<typeof EntryRoleSchema>;

/** One append-only transcript row, ordered by (sessionId, seq) (ARCHITECTURE §6.5). */
export const TranscriptEntrySchema = z.object({
  id: z.string().min(1),
  sessionId: z.string().min(1),
  sessionKind: SessionKindSchema,
  seq: z.number().int().nonnegative(),
  role: EntryRoleSchema,
  parts: z.array(EntryPartSchema),
  createdAt: z.number().int().nonnegative(),
  /** A prompt the host wrote for the model (a nudge, a wake-up); never a bubble. */
  hidden: z.boolean().optional(),
}).strict();
export type TranscriptEntry = z.infer<typeof TranscriptEntrySchema>;

/**
 * GET /agents/:id/transcript. `eventSeq` is the agent's event cursor at the
 * moment the entries were read, so a client can open `/stream?after=` with
 * it and miss nothing between the two requests.
 */
export const TranscriptResponseSchema = z.object({
  entries: z.array(TranscriptEntrySchema),
  eventSeq: z.number().int().nonnegative(),
  busy: z.boolean(),
}).strict();
export type TranscriptResponse = z.infer<typeof TranscriptResponseSchema>;

export const BlobUploadResponseSchema = z.object({ blobId: BlobIdSchema, mimeType: MimeTypeSchema, size: z.number().int().nonnegative() }).strict();
export type BlobUploadResponse = z.infer<typeof BlobUploadResponseSchema>;

export const ErrorResponseSchema = z.object({ error: z.string().min(1) }).strict();
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// --- push to phones (client-app/ANDROID.md §5) ------------------------------

/** What a sender answers for one phone: `unregistered` is a phone that is gone, and its token is forgotten. */
export type SendOutcome = "sent" | "unregistered" | "failed";

/** Whoever does the sending, Firebase itself or a relay in front of it. */
export interface PushSender {
  send(token: string, data: Record<string, string>): Promise<SendOutcome>;
}

/** What the app receives: every value a string, as FCM data messages require. */
export function pushPayloadOf(notification: Notification): Record<string, string> {
  return { agentId: notification.agentId, agentName: notification.agentName, kind: notification.kind, body: notification.body, at: String(notification.at) };
}
